import React, { useEffect } from 'react';

const STEP_PICKER_POPOVER_SELECTOR = '.step-input-selector-popover';

const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// Unchecked radios are skipped because the browser tabs to the checked option only.
const isTabbable = (element: HTMLElement) =>
  element.tabIndex >= 0 &&
  !element.closest('[aria-hidden="true"]') &&
  !(
    element instanceof HTMLInputElement &&
    element.type === 'radio' &&
    !element.checked
  );

export const getTabbableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(
    isTabbable
  );

/**
 * Keeps Tab / Shift+Tab inside the open step picker, flyouts included (they portal into the
 * same popover container).
 * - Moves focus itself in DOM order, wrapping from the last tabbable element to the first and back.
 * - Skips elements that refuse focus (hidden ones the selector can't detect without layout).
 * - Pulls focus back in when it has escaped (e.g. to the page after a flyout closed).
 */
const usePickerFocusTrap = (rootRef: React.RefObject<HTMLElement>): void => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const rootEl = rootRef.current;
      const container =
        rootEl?.closest<HTMLElement>(STEP_PICKER_POPOVER_SELECTOR) ?? rootEl;
      if (!container) return;

      const tabbables = getTabbableElements(container);
      if (tabbables.length === 0) return;
      event.preventDefault();
      const step = event.shiftKey ? -1 : 1;
      const total = tabbables.length;
      const active = document.activeElement as HTMLElement | null;
      const activeIndex = active ? tabbables.indexOf(active) : -1;

      let startIndex: number;
      if (activeIndex !== -1) {
        startIndex = (activeIndex + step + total) % total;
      } else {
        // Focus is outside the list (page, picker root or a flyout box): start from where it sits.
        const followingIndex =
          active && container.contains(active)
            ? tabbables.findIndex(
                (element) =>
                  !!(
                    active.compareDocumentPosition(element) &
                    Node.DOCUMENT_POSITION_FOLLOWING
                  )
              )
            : -1;
        if (followingIndex === -1) {
          startIndex = event.shiftKey ? total - 1 : 0;
        } else {
          startIndex = event.shiftKey
            ? (followingIndex - 1 + total) % total
            : followingIndex;
        }
      }

      // Some matches can't take focus (e.g. antd's visibility-hidden clear button), so skip until one does.
      for (let offset = 0; offset < total; offset += 1) {
        const candidate =
          tabbables[(startIndex + offset * step + total * total) % total];
        candidate.focus();
        if (document.activeElement === candidate) return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [rootRef]);
};

export default usePickerFocusTrap;
